import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { FaFileInvoice, FaArrowLeft, FaDownload } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./FactureDetail.css";

export default function FactureDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [facture, setFacture] = useState(null);
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFacture = async () => {
      try {
        const response = await api.get(`/api/factures/${id}/`);
        setFacture(response.data);
        
        // Les achats sont inclus dans la réponse de la facture
        if (response.data.achats) {
          setAchats(response.data.achats);
        } else {
          // Sinon, récupérer les achats séparément
          try {
            const achatsResponse = await api.get(`/api/achats-produits/?facture=${id}`);
            setAchats(achatsResponse.data);
          } catch (err) {
            console.error("Erreur lors du chargement des achats:", err);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement de la facture:", err);
        let errorMessage = t.errorLoadingInvoice;
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = t.invoiceNotFound;
          } else if (err.response.status === 403) {
            errorMessage = t.noPermissionInvoice;
          } else if (err.response.status >= 500) {
            errorMessage = t.serverError;
          } else {
            errorMessage = err.response.data?.detail || err.response.data?.message || errorMessage;
          }
        }
        setError(errorMessage);
        setLoading(false);
      }
    };

    if (id) {
      fetchFacture();
    } else {
      setError(t.missingInvoiceId);
      setLoading(false);
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "0 FCFA";
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount).replace('XOF', 'FCFA');
  };

  const handleDownloadImage = async () => {
    if (!facture?.facture_image) return;

    try {
      let blob;
      let contentType = '';
      const imageUrl = facture.facture_image;
      
      // Vérifier si c'est une URL absolue ou relative
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        // URL absolue - utiliser fetch avec les headers d'authentification
        const token = localStorage.getItem("access_token");
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await fetch(imageUrl, { headers });
        contentType = response.headers.get('content-type') || '';
        blob = await response.blob();
      } else {
        // URL relative - utiliser l'instance axios (qui gère déjà l'authentification et baseURL)
        const response = await api.get(imageUrl, {
          responseType: 'blob',
        });
        contentType = response.headers['content-type'] || '';
        blob = response.data;
      }
      
      // Créer un lien de téléchargement temporaire
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Déterminer l'extension du fichier depuis le Content-Type ou l'URL
      let extension = 'jpg';
      if (contentType) {
        if (contentType.includes('png')) extension = 'png';
        else if (contentType.includes('jpeg') || contentType.includes('jpg')) extension = 'jpg';
        else if (contentType.includes('gif')) extension = 'gif';
        else if (contentType.includes('webp')) extension = 'webp';
      } else {
        // Essayer de détecter depuis l'URL
        const urlMatch = imageUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        if (urlMatch) extension = urlMatch[1].toLowerCase();
      }
      
      // Nom du fichier avec le numéro de facture (nettoyer les caractères invalides)
      const cleanNumero = facture.numero.replace(/[^a-zA-Z0-9-_]/g, '_');
      const fileName = `facture-${cleanNumero}.${extension}`;
      link.download = fileName;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Libérer l'URL
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur lors du téléchargement de l'image:", err);
      alert(t.errorDownloadingImage);
    }
  };

  if (loading) {
    return (
      <div className="facture-detail-page">
        <div className="loading-container">
          <p>{t.loadingInvoice}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="facture-detail-page">
        <div className="title-badge">
          <FaFileInvoice className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button
            onClick={() => navigate("/factures")}
            className="back-button"
          >
            {t.backToList}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="facture-detail-page">
      <div className="title-badge">
        <FaFileInvoice className="page-title-icon" aria-hidden="true" />
        <span>{t.invoiceDetails}</span>
      </div>

      <div className="facture-detail-actions">
        <button
          onClick={() => navigate("/factures")}
          className="back-button"
        >
          <FaArrowLeft />
          {t.backToList}
        </button>
      </div>

      {facture && (
        <div className="facture-detail-container">
          {/* Informations de la facture */}
          <div className="facture-info-section">
            <h2>{t.invoiceInformation}</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>{t.number}</label>
                <span>{facture.numero}</span>
              </div>
              <div className="info-item">
                <label>{t.supplier}:</label>
                <span>{facture.fournisseur}</span>
              </div>
              <div className="info-item">
                <label>{t.purchaseDate}</label>
                <span>{formatDate(facture.date_achat)}</span>
              </div>
              <div className="info-item">
                <label>{t.totalAmount}</label>
                <span className="montant">{formatCurrency(facture.montant_total)}</span>
              </div>
              {facture.nombre_produits !== undefined && (
                <div className="info-item">
                  <label>{t.numberOfProducts}</label>
                  <span>{facture.nombre_produits}</span>
                </div>
              )}
              {facture.notes && (
                <div className="info-item full-width">
                  <label>{t.notes}</label>
                  <span>{facture.notes}</span>
                </div>
              )}
              {facture.facture_image && (
                <div className="info-item full-width">
                  <div className="image-header">
                    <label>{t.invoiceImage}</label>
                    <button
                      onClick={handleDownloadImage}
                      className="download-image-button"
                      title={t.downloadInvoiceImage}
                    >
                      <FaDownload />
                      <span>{t.download}</span>
                    </button>
                  </div>
                  <div className="image-container">
                    <img src={facture.facture_image} alt="Facture" className="facture-image" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Liste des produits achetés */}
          {achats.length > 0 && (
            <div className="achats-section">
              <h2>{t.purchasedProducts}</h2>
              <div className="achats-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t.product}</th>
                      <th>{t.quantity}</th>
                      <th>{t.unitPrice}</th>
                      <th>{t.totalPrice}</th>
                      <th>{t.expirationDate}</th>
                      <th>{t.lotNumber}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {achats.map((achat) => (
                      <tr key={achat.id}>
                        <td>{achat.produit_nom || `${t.product} #${achat.produit}`}</td>
                        <td>{achat.quantite} {achat.produit_unite || ""}</td>
                        <td>{formatCurrency(achat.prix_unitaire)}</td>
                        <td className="montant">{formatCurrency(achat.prix_total)}</td>
                        <td>{formatDate(achat.date_expiration)}</td>
                        <td>{achat.numero_lot || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="total-label">{t.total}:</td>
                      <td className="montant total">{formatCurrency(facture.montant_total)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}